import { useCallback, useEffect, useState } from 'react'
import {
  DROITS,
  LIBELLE_ROLE,
  droitsEffectifs,
  inviterMembre,
  listerDroitsParRole,
  listerStaff,
  modifierMembre,
  type CleDroit,
  type Droits,
  type DroitsParRole,
} from '../../api/dispatch'
import type { MembreStaff } from '../../api/types'
import { Modale } from '../Modale'
import { BlocIdentifiants } from '../BlocIdentifiants'
import { accesARemettre, type AccesARemettre } from '../../api/identifiants'
import { contactAffiche, validerContact, nomLisible, type ContactValide } from '../../api/contactCompte'
import { ChampTelephone } from '../../../../../../web-commun/connexion-whatsapp'
import { useTelephone } from '../useTelephone'

/**
 * Équipe & droits — TRANSPOSITION du gabarit d'origine.
 *
 * L'écran précédent se réduisait à un tableau en lecture : cinq colonnes, pas
 * de bouton, pas de modale. Tout ce qui FAIT cet écran manquait — inviter,
 * éditer, suspendre, et surtout la grille des droits.
 *
 * Les droits ne sont pas décoratifs : `staff_role_permissions` est la table que
 * lit `has_perm()` derrière la RLS. Ce que l'on coche ici est appliqué par la
 * base, pas seulement par cette page.
 */
const ROLES = ['admin', 'dispatcher', 'comptable', 'support']

const LIBELLE_STATUT: Record<string, [string, string]> = {
  active: ['Actif', 'badge-green'],
  invited: ['Invite', 'badge-yellow'],
  suspended: ['Suspendu', 'badge-gray'],
}

const Check = () => (
  <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <path d="M20 6L9 17l-5-5" />
  </svg>
)

export function EcranEquipe({ monProfileId }: { monProfileId: string | null }) {
  const [membres, setMembres] = useState<MembreStaff[]>([])
  const [parRole, setParRole] = useState<DroitsParRole>({})
  const [erreur, setErreur] = useState<string | null>(null)
  const [edition, setEdition] = useState<MembreStaff | 'nouveau' | null>(null)
  const [identifiants, setIdentifiants] = useState<AccesARemettre | null>(null)

  const charger = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([listerStaff(), listerDroitsParRole()])
      setMembres(m)
      setParRole(r)
      setErreur(null)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Lecture impossible.')
    }
  }, [])

  useEffect(() => {
    void charger()
  }, [charger])

  async function changerStatut(m: MembreStaff, statut: string) {
    if (statut === 'suspended' && !window.confirm('Suspendre ce membre ?')) return
    try {
      await modifierMembre(m.id, { status: statut })
      await charger()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Modification impossible.')
    }
  }

  const actifs = membres.filter((m) => m.statut !== 'suspended').length

  return (
    <>
      {erreur && <p style={{ color: 'var(--danger)', fontSize: 13 }}>{erreur}</p>}

      <div className="card">
        <div className="card-header">
          Equipe &amp; droits
          <span style={{ color: 'var(--n400)', fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
            {actifs} membre{actifs > 1 ? 's' : ''} actif{actifs > 1 ? 's' : ''} · créés par
            invitation
          </span>
          <div className="spacer" style={{ flex: 1 }} />
          <button className="btn btn-accent btn-sm" onClick={() => setEdition('nouveau')}>
            + Inviter un membre
          </button>
        </div>
        <div className="card-body flush">
          <table>
            <thead>
              <tr>
                <th>Membre</th>
                <th>Contact</th>
                <th>Role</th>
                <th>Droits</th>
                <th>Statut</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {membres.map((m) => {
                const [libelle, classe] = LIBELLE_STATUT[m.statut] ?? ['—', 'badge-gray']
                const nb = Object.values(droitsEffectifs(m, parRole)).filter(Boolean).length
                /* On masque seulement SA PROPRE ligne d'actions. Le vrai
                   garde-fou est ailleurs : le serveur refuse de retirer le
                   dernier admin actif et refuse qu'on se modifie soi-même.
                   Proposer un bouton dont on sait qu'il sera refusé n'apprend
                   rien à personne. */
                const moi = Boolean(m.profileId) && m.profileId === monProfileId
                return (
                  <tr key={m.id}>
                    <td>
                      <strong>{m.nom}</strong>
                      {m.intitule && (
                        <div style={{ fontSize: 11, color: 'var(--n400)' }}>{m.intitule}</div>
                      )}
                    </td>
                    <td style={{ color: 'var(--n400)' }}>{contactAffiche(m.email, m.telephone) || '—'}</td>
                    <td>
                      <span className="badge badge-orange">{LIBELLE_ROLE[m.role] ?? m.role}</span>
                    </td>
                    <td style={{ color: 'var(--n400)' }}>
                      {nb} droit{nb > 1 ? 's' : ''}
                    </td>
                    <td>
                      <span className={`badge ${classe}`}>{libelle}</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => setEdition(m)}>
                          Editer
                        </button>
                        {moi ? (
                          <span style={{ fontSize: 11.5, color: 'var(--n400)' }}>vous</span>
                        ) : m.statut === 'suspended' ? (
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => void changerStatut(m, 'active')}
                          >
                            Reactiver
                          </button>
                        ) : (
                          <button
                            className="btn btn-danger-sm btn-sm"
                            onClick={() => void changerStatut(m, 'suspended')}
                          >
                            Suspendre
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ background: 'var(--accent-light)', border: '1px solid var(--accent-light)' }}>
        <div
          className="card-body"
          style={{ fontSize: 12.5, color: 'var(--n700)', display: 'flex', gap: 10, alignItems: 'flex-start' }}
        >
          <span style={{ color: 'var(--or-t)', flex: 'none' }} />
          <div>
            Les membres sont <b>créés par vous</b>, jamais par inscription libre.{' '}
            <b>Aucun message n'est envoyé</b> : avec un e-mail, un mot de passe temporaire s'affiche
            une seule fois — c'est à vous de le transmettre ; avec un numéro WhatsApp seul, la personne
            se connecte avec un code reçu sur WhatsApp. Le rôle et les droits que vous fixez ici
            sont appliqués par la base, pas seulement par cet écran : un membre ne voit que les
            onglets auxquels il a droit, et le serveur refuse le reste.
          </div>
        </div>
      </div>

      {edition && (
        <ModaleMembre
          membre={edition === 'nouveau' ? null : edition}
          parRole={parRole}
          surFermer={() => setEdition(null)}
          surEnregistrer={async (v) => {
            if (edition === 'nouveau') {
              const r = await inviterMembre(v)
              setEdition(null)
              /* Compte déjà existant : pas de mot de passe temporaire, mais on le dit. */
              if (r) setIdentifiants(accesARemettre({ ...r, email: r.email ?? v.contact.email, phone: r.phone ?? v.contact.phone }))
            } else {
              await modifierMembre(edition.id, {
                role: v.role,
                title: v.intitule ?? '',
                permissions: v.permissions,
              })
              setEdition(null)
            }
            await charger()
          }}
        />
      )}

      {/* ⚠️ Affiché UNE SEULE FOIS. Rien ne le stocke, rien ne peut le
          réafficher : si l'admin ferme sans noter, il faut refaire le compte. */}
      {identifiants && (
        <Modale titre="Membre créé" ouverte surFermer={() => setIdentifiants(null)}>
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            <BlocIdentifiants
              acces={identifiants}
              introduction="Transmettez ces accès — la personne se connecte à la console puis change son mot de passe :"
            />
            <button className="btn btn-accent" onClick={() => setIdentifiants(null)}>
              Termine
            </button>
          </div>
        </Modale>
      )}
    </>
  )
}

function ModaleMembre({
  membre,
  parRole,
  surFermer,
  surEnregistrer,
}: {
  membre: MembreStaff | null
  parRole: DroitsParRole
  surFermer: () => void
  surEnregistrer: (v: {
    nom: string
    contact: ContactValide
    role: string
    intitule: string | null
    permissions: Droits
  }) => Promise<void>
}) {
  const [nom, setNom] = useState('')
  const [email, setEmail] = useState(membre?.email ?? '')
  const [telephone, setTelephone] = useTelephone()
  const [champContact, setChampContact] = useState<'email' | 'telephone' | 'contact' | null>(null)
  const [intitule, setIntitule] = useState(membre?.intitule ?? '')
  const [role, setRole] = useState(membre?.role ?? 'dispatcher')
  const [brouillon, setBrouillon] = useState<Droits>(() =>
    membre
      ? droitsEffectifs(membre, parRole)
      : Object.fromEntries((parRole.dispatcher ?? []).map((k) => [k, true])),
  )
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)

  /** Changer de rôle REMET les cases sur celles du rôle — pas de fusion. */
  function choisirRole(r: string) {
    setRole(r)
    setBrouillon(Object.fromEntries((parRole[r] ?? []).map((k) => [k, true])))
  }

  async function enregistrer() {
    /* Création : au moins un e-mail OU un numéro WhatsApp, chacun valide s'il est renseigné. En édition, le contact
       n'est pas modifiable ici (il identifie le compte). */
    const contact = membre
      ? ({ ok: true, contact: { email: membre.email || null, phone: membre.telephone || null, pays: null } } as const)
      : validerContact({ email, telephone })
    if (!contact.ok) {
      setErreur(contact.message)
      setChampContact(contact.champ)
      return
    }
    setChampContact(null)
    /* On n'envoie QUE les écarts au rôle. Envoyer les droits complets figerait
       le membre : une évolution ultérieure des droits du rôle ne le
       toucherait plus jamais. */
    const ecarts: Droits = {}
    for (const d of DROITS) {
      const parDefaut = (parRole[role] ?? []).includes(d.cle)
      if (Boolean(brouillon[d.cle]) !== parDefaut) ecarts[d.cle] = Boolean(brouillon[d.cle])
    }
    setEnvoi(true)
    setErreur(null)
    try {
      await surEnregistrer({
        nom: nom.trim() || nomLisible(null, contact.contact.email, contact.contact.phone),
        contact: contact.contact,
        role,
        intitule: intitule.trim() || null,
        permissions: ecarts,
      })
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Enregistrement impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <Modale
      titre={membre ? `Editer ${membre.nom}` : 'Inviter un membre'}
      ouverte
      surFermer={surFermer}
    >
      {!membre && (
        <>
          <label>Nom complet</label>
          <input placeholder="ex. Sekou Diarra" value={nom} onChange={(e) => setNom(e.target.value)} />
        </>
      )}

      {membre ? (
        <>
          <label>Contact</label>
          <input value={contactAffiche(membre.email, membre.telephone)} disabled />
        </>
      ) : (
        <>
          <label>E-mail (recommandé)</label>
          <input
            type="email"
            placeholder="prenom@swifteats.ml"
            value={email}
            aria-invalid={champContact === 'email' || champContact === 'contact' ? true : undefined}
            onChange={(e) => setEmail(e.target.value)}
          />
          <ChampTelephone
            valeur={telephone}
            surChangement={setTelephone}
            montrerErreur={champContact === 'telephone'}
            aide="Au Mali, c'est souvent le plus pratique : la personne reçoit un code sur WhatsApp à chaque connexion."
          />
          <p style={{ fontSize: 11.5, color: 'var(--n400)', margin: '4px 0 0' }}>
            Renseignez au moins l'un des deux : l'e-mail ou le numéro WhatsApp.
          </p>
        </>
      )}

      <label>Intitule de poste</label>
      <input
        placeholder="ex. Dispatch nuit"
        value={intitule}
        onChange={(e) => setIntitule(e.target.value)}
      />

      <label>Role</label>
      <select value={role} onChange={(e) => choisirRole(e.target.value)}>
        {ROLES.map((r) => (
          <option key={r} value={r}>
            {LIBELLE_ROLE[r]}
            {r === 'admin' ? ' (tous droits)' : ''}
          </option>
        ))}
      </select>

      <label>Droits (ajustables au-dela du role)</label>
      <div className="perm-grid">
        {DROITS.map((d) => (
          <div
            key={d.cle}
            className={`perm-tog ${brouillon[d.cle as CleDroit] ? 'on' : ''}`}
            onClick={() => setBrouillon((b) => ({ ...b, [d.cle]: !b[d.cle as CleDroit] }))}
          >
            <span className="pbox">
              <Check />
            </span>{' '}
            {d.libelle}
          </div>
        ))}
      </div>

      {erreur && <p style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 8 }}>{erreur}</p>}

      <button
        className="btn btn-accent"
        style={{ marginTop: 16 }}
        disabled={envoi}
        onClick={() => void enregistrer()}
      >
        {membre ? 'Enregistrer' : 'Créer le compte'}
      </button>
    </Modale>
  )
}
